import React from 'react';
import './GrammarTable.css';
import { grammarData } from '../../data/grammarData';
import VerbsList from '../VerbsList/VerbsList';
import ExamplesModal from '../Modal/ExamplesModal';

const GrammarTable = () => {
  const [modalData, setModalData] = React.useState(null);
  const [expandedSections, setExpandedSections] = React.useState({});
  const [lastClickedElement, setLastClickedElement] = React.useState(null);
  const [showIndex, setShowIndex] = React.useState(false);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [highlightedButton, setHighlightedButton] = React.useState(null);
  const [expandingSection, setExpandingSection] = React.useState(null);

  // Effect para mostrar/ocultar botón de scroll top y actualizar progreso
  React.useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          const progress = Math.min((scrollTop / docHeight) * 100, 100);
          
          setShowScrollTop(scrollTop > 400);
          setScrollProgress(progress);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openModal = (type, event) => {
    console.log(type);
    // Guardar referencia simple del botón sin manipulación del DOM
    const element = event.target;
    const rect = element.getBoundingClientRect();
    const scrollY = window.scrollY;
    
    setLastClickedElement({
      buttonId: `${type}-btn`,
      position: rect.top + scrollY - 100
    });
    setModalData(type);
  };

  const closeModal = () => {
    setModalData(null);
    // Activar highlight del botón usando estado de React
    if (lastClickedElement) {
      setHighlightedButton(lastClickedElement.buttonId);
      
      // Scroll suave sin manipulación directa del DOM
      setTimeout(() => {
        window.scrollTo({
          top: lastClickedElement.position,
          behavior: 'smooth'
        });
      }, 100);
      
      // Quitar highlight después de un tiempo
      setTimeout(() => {
        setHighlightedButton(null);
      }, 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[sectionId];
      
      if (isCurrentlyExpanded) {
        // Si está expandida, simplemente cerrarla
        return {
          ...prev,
          [sectionId]: false
        };
      } else {
        // Indicar que esta sección se está expandiendo
        setExpandingSection(sectionId);
        
        // Primero cerrar todas las demás y abrir esta
        const newSections = {};
        Object.keys(prev).forEach(key => {
          newSections[key] = false;
        });
        newSections[sectionId] = true;
        
        // Después de expandir, hacer scroll para que el título quede visible
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            // Buscar específicamente el header de la sección
            const header = element.querySelector('.category-header');
            if (header) {
              const headerRect = header.getBoundingClientRect();
              const headerTop = headerRect.top + window.scrollY - 80; // 80px de offset desde arriba
              
              window.scrollTo({
                top: headerTop,
                behavior: 'smooth'
              });
            }
          }
          setExpandingSection(null); // Limpiar el estado de expansión
        }, 100); // Delay más corto para que el contenido se expanda primero
        
        return newSections;
      }
    });
  };

  // Función para scroll a sección específica
  const scrollToSection = (sectionId) => {
    const isCurrentlyExpanded = expandedSections[sectionId];
    
    // Si no está expandida, indicar que se está expandiendo
    if (!isCurrentlyExpanded) {
      setExpandingSection(sectionId);
    }
    
    // Si no está expandida, expandirla primero
    if (!isCurrentlyExpanded) {
      setExpandedSections(prev => {
        const newSections = {};
        Object.keys(prev).forEach(key => {
          newSections[key] = false;
        });
        newSections[sectionId] = true;
        return newSections;
      });
    }
    
    // Hacer scroll después de expandir para que el título quede visible
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Buscar específicamente el header de la sección
        const header = element.querySelector('.category-header');
        if (header) {
          const headerRect = header.getBoundingClientRect();
          const headerTop = headerRect.top + window.scrollY - 80;
          
          window.scrollTo({
            top: headerTop,
            behavior: 'smooth'
          });
        }
      }
      setExpandingSection(null); // Limpiar el estado de expansión
    }, isCurrentlyExpanded ? 0 : 100);
    
    setShowIndex(false);
  };

  // Definir todas las secciones para el índice
  const sections = [
    { id: 'conditionals', title: 'Condicionales y Comparativos', icon: '🎯' },
    { id: 'tagQuestions', title: 'Tag Questions & Countables', icon: '❓' },
    { id: 'verbTenses', title: 'Tiempos Verbales', icon: '⏰' },
    { id: 'modalVerbs', title: 'Modal Verbs', icon: '🔮' },
    { id: 'phrasalVerbs', title: 'Phrasal Verbs', icon: '🚀' },
    { id: 'irregularVerbs', title: 'Verbos Irregulares', icon: '📝' },
    { id: 'placesToGoOut', title: 'Lugares para Salir', icon: '🏙️' },
    { id: 'numbersAndDates', title: 'Números y Fechas', icon: '📅' },
    { id: 'additionsToRemark', title: 'Additions to Remark', icon: '➕' },
    { id: 'clothingVocabulary', title: 'Vocabulario de Ropa', icon: '👗' },
    { id: 'comparativesSuperlatives', title: 'Comparativos y Superlativos', icon: '📊' },
    { id: 'restaurantMenu', title: 'Menú de Restaurante', icon: '🍽️' },
    { id: 'tagQuestionsExpanded', title: 'Tag Questions Avanzado', icon: '❓' },
    { id: 'foodPreparation', title: 'Preparación de Comida', icon: '👨‍🍳' },
    { id: 'conditionalsExpanded', title: 'Conditionals Completos', icon: '🔄' },
    { id: 'exerciseFitness', title: 'Ejercicio y Fitness', icon: '💪' },
    { id: 'tvEntertainment', title: 'TV y Entretenimiento', icon: '📺' },
    { id: 'medicalVocabulary', title: 'Vocabulario Médico', icon: '🏥' },
    { id: 'perfectTenses', title: 'Tiempos Perfectos', icon: '⏰' }
  ];

  const CollapsibleSection = ({ id, title, children, defaultExpanded = false }) => {
    const isExpanded = expandedSections[id] ?? defaultExpanded;
    const isExpanding = expandingSection === id;
    
    return (
      <div id={id} className={`category-section collapsible ${isExpanding ? 'expanding' : ''}`}>
        <div 
          className={`category-header ${isExpanding ? 'expanding' : ''}`}
          onClick={() => toggleSection(id)}
        >
          <h2 className="category-title">
            {title}
            {isExpanding && <span className="expanding-indicator"> ⏳</span>}
          </h2>
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
        <div className={`category-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
          {children}
        </div>
      </div>
    );
  };

  const renderTable = (data, headers) => (
    <div className="table-container">
      {/* Tabla horizontal para desktop */}
      <table>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td className="tema">{item.tema}</td>
              <td className="estructura">{item.estructura}</td>
              <td className="ejemplo">{item.ejemplo}</td>
              <td className="traduccion">{item.traduccion}</td>
              <td className="uso">{item.uso}</td>
              <td>
                <button 
                  id={`${item.id}-btn`}
                  className={`btn-ejemplos ${highlightedButton === `${item.id}-btn` ? 'highlighted' : ''}`}
                  onClick={(e) => openModal(item.id, e)}
                >
                  Ver más ejemplos
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Tabla vertical para móviles y tablets */}
      <div className="table-vertical">
        {data.map((item, index) => (
          <div key={index} className="table-row-card">
            <div className="card-content">
              <div className="card-field">
                <span className="field-label">Tema</span>
                <div className="field-content">
                  <span className="tema">{item.tema}</span>
                </div>
              </div>
              
              <div className="card-field">
                <span className="field-label">Estructura</span>
                <div className="field-content">
                  <span className="estructura">{item.estructura}</span>
                </div>
              </div>
              
              <div className="card-field">
                <span className="field-label">Ejemplo</span>
                <div className="field-content">
                  <span className="ejemplo">{item.ejemplo}</span>
                </div>
              </div>
              
              <div className="card-field">
                <span className="field-label">Traducción</span>
                <div className="field-content">
                  <span className="traduccion">{item.traduccion}</span>
                </div>
              </div>
              
              <div className="card-field">
                <span className="field-label">Uso</span>
                <div className="field-content">
                  <span className="uso">{item.uso}</span>
                </div>
              </div>
              
              <div className="card-field">
                <span className="field-label">Más Ejemplos</span>
                <div className="field-content">
                  <button 
                    id={`${item.id}-btn`}
                    className={`btn-ejemplos ${highlightedButton === `${item.id}-btn` ? 'highlighted' : ''}`}
                    onClick={(e) => openModal(item.id, e)}
                  >
                    Ver más ejemplos
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="container">
      {/* BARRA DE PROGRESO DE LECTURA */}
      <div className="reading-progress">
        <div 
          className="progress-bar" 
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      {/* ÍNDICE DE NAVEGACIÓN STICKY */}
      <div className="navigation-index">
        <button 
          className={`index-toggle ${showIndex ? 'active' : ''}`}
          onClick={() => setShowIndex(!showIndex)}
        >
          📋 Índice de Contenido {showIndex ? '▲' : '▼'}
        </button>
        
        {showIndex && (
          <div className="index-content">
            <div className="index-grid">
              {sections.map((section, index) => (
                <button
                  key={section.id}
                  className={`index-item ${expandedSections[section.id] ? 'expanded' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <span className="index-icon">{section.icon}</span>
                  <span className="index-title">{section.title}</span>
                  <span className="index-number">{index + 1}</span>
                </button>
              ))}
            </div>
            <button 
              className="close-index"
              onClick={() => setShowIndex(false)}
            >
              ✕ Cerrar Índice
            </button>
          </div>
        )}
      </div>
      {/* TABLA DE CONDICIONALES Y COMPARATIVOS */}
      <CollapsibleSection 
        id="conditionals" 
        title="🎯 CONDICIONALES Y COMPARATIVOS"
        defaultExpanded={false}
      >
        {renderTable(grammarData.conditionals, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TABLA DE TAG QUESTIONS Y COUNTABLES */}
      <CollapsibleSection 
        id="tagQuestions" 
        title="❓ TAG QUESTIONS & COUNTABLES/UNCOUNTABLES"
        defaultExpanded={false}
      >
        {renderTable(grammarData.tagQuestions, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TABLA DE TIEMPOS VERBALES */}
      <CollapsibleSection 
        id="verbTenses" 
        title="⏰ TIEMPOS VERBALES PRINCIPALES"
        defaultExpanded={false}
      >
        {renderTable(grammarData.verbTenses, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TABLA DE MODAL VERBS */}
      <CollapsibleSection 
        id="modalVerbs" 
        title="🔮 MODAL VERBS OF DEDUCTION"
        defaultExpanded={false}
      >
        {renderTable(grammarData.modalVerbs, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
        
        <h3 style={{ color: '#764ba2', marginTop: '30px', marginBottom: '15px' }}>
          🕐 MODAL VERBS - DEDUCCIÓN EN PASADO
        </h3>
        {renderTable(grammarData.modalVerbsPast, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TABLA DE PHRASAL VERBS */}
      <CollapsibleSection 
        id="phrasalVerbs" 
        title="🚀 PHRASAL VERBS MÁS COMUNES"
        defaultExpanded={false}
      >
        {renderTable(grammarData.phrasalVerbs, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* VERBOS IRREGULARES */}
      <CollapsibleSection 
        id="irregularVerbs" 
        title="📝 VERBOS IRREGULARES TOP 20"
        defaultExpanded={false}
      >
        <VerbsList />
      </CollapsibleSection>

      {/* PLACES TO GO OUT - NEW SECTION */}
      <CollapsibleSection 
        id="placesToGoOut" 
        title="🏙️ LUGARES PARA SALIR Y DIVERTIRSE"
        defaultExpanded={false}
      >
        {renderTable(grammarData.placesToGoOut, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* NUMBERS AND DATES - NEW SECTION */}
      <CollapsibleSection 
        id="numbersAndDates" 
        title="📅 NÚMEROS, FECHAS Y DÍAS"
        defaultExpanded={false}
      >
        {renderTable(grammarData.numbersAndDates, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* ADDITIONS TO REMARK - NEW SECTION */}
      <CollapsibleSection 
        id="additionsToRemark" 
        title="➕ ADDITIONS TO REMARK (TOO, EITHER, SO, NEITHER)"
        defaultExpanded={false}
      >
        {renderTable(grammarData.additionsToRemark, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* CLOTHING VOCABULARY - NEW SECTION */}
      <CollapsibleSection 
        id="clothingVocabulary" 
        title="👗 VOCABULARIO DE ROPA Y ACCESORIOS"
        defaultExpanded={false}
      >
        {renderTable(grammarData.clothingVocabulary, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* COMPARATIVES AND SUPERLATIVES - EXPANDED */}
      <CollapsibleSection 
        id="comparativesSuperlatives" 
        title="📊 COMPARATIVOS Y SUPERLATIVOS (COMPLETO)"
        defaultExpanded={false}
      >
        {renderTable(grammarData.comparativesSuperlatives, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* RESTAURANT MENU - NEW SECTION */}
      <CollapsibleSection 
        id="restaurantMenu" 
        title="🍽️ MENÚ DE RESTAURANTE Y COMIDA"
        defaultExpanded={false}
      >
        {renderTable(grammarData.restaurantMenu, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TAG QUESTIONS - EXPANDED */}
      <CollapsibleSection 
        id="tagQuestionsExpanded" 
        title="❓ TAG QUESTIONS COMPLETAS (AVANZADO)"
        defaultExpanded={false}
      >
        {renderTable(grammarData.tagQuestionsExpanded, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* FOOD PREPARATION - NEW SECTION */}
      <CollapsibleSection 
        id="foodPreparation" 
        title="👨‍🍳 PREPARACIÓN DE COMIDA Y COCINA"
        defaultExpanded={false}
      >
        {renderTable(grammarData.foodPreparation, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* CONDITIONALS - ALL THREE TYPES EXPANDED */}
      <CollapsibleSection 
        id="conditionalsExpanded" 
        title="🔄 CONDITIONALS COMPLETOS (1st, 2nd, 3rd + MIXED)"
        defaultExpanded={false}
      >
        {renderTable(grammarData.conditionalsExpanded, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* EXERCISE AND FITNESS - NEW SECTION */}
      <CollapsibleSection 
        id="exerciseFitness" 
        title="💪 EJERCICIO Y FITNESS"
        defaultExpanded={false}
      >
        {renderTable(grammarData.exerciseFitness, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TV SHOWS AND ENTERTAINMENT - NEW SECTION */}
      <CollapsibleSection 
        id="tvEntertainment" 
        title="📺 TV Y ENTRETENIMIENTO"
        defaultExpanded={false}
      >
        {renderTable(grammarData.tvEntertainment, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* MEDICAL VOCABULARY - NEW SECTION */}
      <CollapsibleSection 
        id="medicalVocabulary" 
        title="🏥 VOCABULARIO MÉDICO COMPLETO"
        defaultExpanded={false}
      >
        {renderTable(grammarData.medicalVocabulary, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* PERFECT TENSES - NEW SECTION */}
      <CollapsibleSection 
        id="perfectTenses" 
        title="⏰ TIEMPOS PERFECTOS (PRESENTE Y PASADO)"
        defaultExpanded={false}
      >
        {renderTable(grammarData.perfectTenses, [
          "Tema", "Estructura", "Ejemplo", "Traducción", "Uso", "Más Ejemplos"
        ])}
      </CollapsibleSection>

      {/* TIPS BOX - ACTUALIZADO CON NUEVO CONTENIDO */}
      <div className="tips-box">
        <h3>🎯 SÚPER TRUCOS PARA RECORDAR TODO EL CONTENIDO</h3>
        <p style={{ fontSize: '1.1em', color: '#2d3436' }}>
          <strong>🔸 Conditionals:</strong> 1st = Probable | 2nd = Sueño | 3rd = Ya fue | Mixed = Pasado → Presente<br />
          <strong>🔸 Tag Questions:</strong> Positivo → Negativo | Negativo → Positivo<br />
          <strong>🔸 Perfect Tenses:</strong> Present Perfect = Experiencia/Conexión presente | Past Perfect = Antes de algo en pasado<br />
          <strong>🔸 Comparatives:</strong> Cortos +er/est | Largos more/most | Irregulares = good/better/best<br />
          <strong>🔸 Places to Go:</strong> City = entertainment | Outside = nature activities<br />
          <strong>🔸 Clothing:</strong> Head/Upper/Lower/Footwear/Underwear/Formal/Jewelry<br />
          <strong>🔸 Restaurant:</strong> Appetizers → Salads → Entrees → Sides → Desserts → Beverages<br />
          <strong>🔸 Medical:</strong> Workers/Body parts/Symptoms/Ailments/Injuries/Medicine/Advice<br />
          <strong>🔸 Additions:</strong> Positive = too/so | Negative = either/neither<br />
          <strong>🔸 Dates:</strong> in + month/year | on + complete date | ordinal numbers para fechas<br />
          <strong>🔸 Cooking:</strong> Measurements → Verbs → Sequence → Fractions<br />
          <strong>🔸 Fitness:</strong> Equipment + Activities + Movements + Terms + Goals<br />
          <strong>🔸 TV:</strong> Genres + Movies + Vocabulary + Preferences<br />
          <strong>🔸 Phrasal Verbs:</strong> Verbo + Partícula = Nuevo significado
        </p>
      </div>

      {/* Modal */}
      {modalData && <ExamplesModal type={modalData} onClose={closeModal} />}
    </div>
  );
};

export default GrammarTable;
